import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'

interface RoleInsight {
  currentPerceptions?: Record<string, string> | string
  currentTendencies?: Record<string, string> | string
  aspirations?: Record<string, string> | string
  keyNotes?: string
}

interface RoleInsightsProps {
  insights: Record<string, RoleInsight> | null | undefined
}

export function RoleInsightsSection({ insights }: RoleInsightsProps) {
  if (!insights || typeof insights !== 'object') return null

  // Filter out any invalid entries and normalize data format
  const validInsights = Object.entries(insights).filter(([_, insight]) => {
    return insight && typeof insight === 'object' && (
      insight.currentTendencies ||
      insight.currentPerceptions ||
      insight.aspirations ||
      insight.keyNotes
    )
  }).map(([role, insight]) => {
    // Handle both old and new data formats
    const currentPerceptions = (() => {
      if (typeof insight.currentPerceptions === 'object' && insight.currentPerceptions) {
        return Object.entries(insight.currentPerceptions)
          .map(([spectrum, desc]) => `${spectrum}: ${desc}`)
          .join('\n')
      }
      if (typeof insight.currentTendencies === 'object' && insight.currentTendencies) {
        return Object.entries(insight.currentTendencies)
          .map(([aspect, desc]) => `${aspect}: ${desc}`)
          .join('\n')
      }
      return insight.currentPerceptions || insight.currentTendencies || ''
    })()

    const aspirations = (() => {
      if (typeof insight.aspirations === 'object' && insight.aspirations) {
        return Object.entries(insight.aspirations)
          .map(([spectrum, desc]) => `${spectrum}: ${desc}`)
          .join('\n')
      }
      return insight.aspirations || ''
    })()

    return [role, {
      ...insight,
      currentPerceptions,
      aspirations,
    }] as [string, RoleInsight]
  })

  if (validInsights.length === 0) return null

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {validInsights.map(([role, insight]) => (
        <Card key={role} className="h-full">
          <CardHeader>
            <CardTitle>{role}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Perceptions */}
            {insight.currentPerceptions && (
              <div>
                <p className="text-sm font-medium">Current Perceptions</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {typeof insight.currentPerceptions === 'string' 
                    ? insight.currentPerceptions 
                    : JSON.stringify(insight.currentPerceptions)}
                </p>
              </div>
            )}

            {/* Aspirations */}
            {insight.aspirations && (
              <div>
                <p className="text-sm font-medium">Aspirations</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {typeof insight.aspirations === 'string' 
                    ? insight.aspirations 
                    : JSON.stringify(insight.aspirations)}
                </p>
              </div>
            )}

            {/* Key Notes */}
            {insight.keyNotes && (
              <div>
                <p className="text-sm font-medium">Key Notes</p>
                <p className="text-sm text-muted-foreground">
                  {insight.keyNotes}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}